import CSS from 'csstype';

import { SortIconKey } from '~/src/dash/components/sorts/common';
import { spacing } from '~/src/dash/publicaUI';
import { IMenuList } from '~/src/dash/types/menuList';
import { convertLargeNumbersToRoundedString } from '~/src/dash/utils/basic';

export type ListViewTableValueType =
  | 'array'
  | 'boolean'
  | 'component'
  | 'date'
  | 'number'
  | 'object'
  | 'string';

export type ListViewTableColumnHeaderRenderType =
  | 'actions'
  | 'centered_text'
  | 'date'
  | 'left_column'
  | 'name'
  | 'number'
  | 'number_small'
  | 'number_select'
  | 'right_text'
  | 'string'
  | 'simple_switch'
  | 'switch';

export const COLUMN_HEADER_STYLE = {
  actions: {
    maxWidth: '160px',
    minWidth: '128px',
    textAlign: 'center',
  },
  centered_text: {
    textAlign: 'center',
    verticalAlign: 'center',
  },
  right_text: {
    textAlign: 'right',
    paddingLeft: spacing.large,
  },
  date: {
    maxWidth: '192px',
    minWidth: '176px',
    paddingLeft: spacing.large,
    textAlign: 'left',
  },
  left_column: {
    maxWidth: '240px',
    minWidth: '192px',
    left: 0,
    textAlign: 'left',
    top: 0,
  },
  name: {
    left: 0,
    position: 'sticky',
    textAlign: 'left',
    top: 0,
    width: '100%',
    zIndex: 1,
  },
  number: {
    maxWidth: '172px',
    minWidth: '160px',
    paddingLeft: spacing.medium,
    textAlign: 'right',
  },
  number_small: {
    maxWidth: '128px',
    minWidth: '96px',
    paddingLeft: spacing.medium,
    textAlign: 'right',
  },
  number_select: {
    maxWidth: '112px',
    minWidth: '96px',
    paddingLeft: spacing.medium,
    textAlign: 'right',
  },
  string: {
    paddingLeft: spacing.large,
  },
  simple_switch: {
    maxWidth: '128px',
    minWidth: '112px',
    textAlign: 'right',
  },
  switch: {
    minWidth: 'auto',
    width: '72px',
  },
} as Record<ListViewTableColumnHeaderRenderType, CSS.Properties>;

export type ListViewTableDataCellRenderType =
  | 'abbreviated_number' // abbreviate and round number
  | 'actions'
  | 'date'
  | 'name'
  | 'name_capitalize'
  | 'name_link'
  | 'number'
  | 'percent'
  | 'right_text'
  | 'simple_switch'
  | 'string';

export const CELL_STYLE = {
  actions: {
    paddingRight: '30px',
  },
  date: {
    paddingLeft: spacing.large,
    textAlign: 'left',
  },
  name_capitalize: {
    textTransform: 'capitalize',
  },
  name_link: {
    wordBreak: 'break-all',
  },
  number: {},
  right_text: {
    paddingLeft: spacing.large,
    paddingRight: spacing.none,
    textAlign: 'right',
  },
  simple_switch: {
    textAlign: 'right',
  },
  string: {
    paddingLeft: spacing.large,
    textAlign: 'left',
  },
} as Record<ListViewTableDataCellRenderType, CSS.Properties>;

// render value only affects how the value is displayed, where as format value affects how an item is sorted and stored
export const renderValue = (
  value: string | number | boolean,
  type: ListViewTableDataCellRenderType | undefined,
) => {
  switch (type) {
    case 'abbreviated_number':
      return convertLargeNumbersToRoundedString((value as number) || 0);
    case 'percent':
      return `${Number(value).toFixed(2)} %`;
    default:
      return value;
  }
};

export interface ICustomRenderHeaderJSXProps {
  label: string;
  column: IListViewTableColumnInfo;
}

export interface ICustomRenderCellJSXProps {
  value: string | number | boolean | Array<string | number>;
  row: Record<string, IListViewTableDataCell>;
}

export interface IListViewTableColumnInfo {
  cellJSX?: (props: ICustomRenderCellJSXProps) => any; // send custom component to render cell and value. search DateTimeRender for an example
  cellRenderType?: ListViewTableDataCellRenderType; // adds preset rendering
  cellStyle?: CSS.Properties; // add css to value cell
  defaultOrderDirection?: Order;
  disableSort?: boolean; // will disable header click to sort and hide arrow
  formatValue?: (value: any) => any; // will reformat value for sorting before render
  headerJSX?: (props: ICustomRenderHeaderJSXProps) => any; // send custom component to render column header
  headerRenderType?: ListViewTableColumnHeaderRenderType; // adds preset rendering
  headerStyle?: CSS.Properties; // add css to header column cell
  headerTooltipText?: string;
  isDate?: boolean;
  isDefaultActive?: boolean; // true will display column on initial load
  isHidden?: boolean; // true will hide column from active columns dropdown
  isNumeric?: boolean;
  isSearchable?: boolean; // true will allow search bar to function on column data. only works if all rows are available
  isSuperUserOnly?: boolean; // true will make it visible only to super users
  key: string; // must match original key
  label: string; // text displayed on column header
  orderByKey?: string; // key as is in api order by
  requestKey: string; // key as is in api request object
  responseKey: string; // key as is in api response object
  sortIcon?: SortIconKey;
  sortLabel?: string; // text displayed on sort row pill
  parentKey?: string; // key if associated to another column. ex: trends and the origin
  trendKey?: string; // the associated trend column key
  valueType: ListViewTableValueType;
}

export interface IListViewTableColumns {
  ID: IListViewTableColumnInfo;
  Name: IListViewTableColumnInfo;
  [key: string]: IListViewTableColumnInfo;
}

export type IListViewTableRow = Record<string, IListViewTableDataCell>;

export type Order = 'asc' | 'desc';
export type RowsPerPage = -1 | 5 | 10 | 25 | 50 | 100;

export interface IListViewTableDataCell {
  cellStyle?: CSS.Properties;
  jsx?: any;
  renderType?: ListViewTableDataCellRenderType;
  value: any;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T, valueType: ListViewTableValueType) {
  // @ts-ignore
  let aValue = a[orderBy].value;
  // @ts-ignore
  let bValue = b[orderBy].value;
  if (aValue === '') {
    return -1;
  }
  if (bValue === '') {
    return 1;
  }
  switch (valueType) {
    case 'string':
      aValue = aValue.toUpperCase();
      bValue = bValue.toUpperCase();
    default:
      break;
  }
  if (bValue < aValue) {
    return -1;
  }
  if (bValue > aValue) {
    return 1;
  }
  return 0;
}

export function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
  valueType: ListViewTableValueType,
): (a: any, b: any) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy, valueType)
    : (a, b) => -descendingComparator(a, b, orderBy, valueType);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
export function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export const extractDefaultActiveColumnKeys = (
  columns: Record<string, IListViewTableColumnInfo>,
) => {
  const keys = [] as string[];
  Object.values(columns).forEach((column) => {
    if (column.isDefaultActive) {
      keys.push(column.key);
    }
  });
  return keys;
};

export const extractSearchableColumnKeys = (columns: Record<string, IListViewTableColumnInfo>) => {
  const keys = [] as string[];
  Object.values(columns).forEach((column) => {
    if (column.isSearchable) {
      keys.push(column.key);
    }
  });
  return keys;
};

export const filterActiveColumnKeys = (
  activeColumnKeys: string[],
  columns: IListViewTableColumns,
  isSuperUser: boolean,
) => {
  const filteredKeys: string[] = [];
  for (const key of activeColumnKeys) {
    if (columns[key].isSuperUserOnly && !isSuperUser) {
      continue;
    }
    filteredKeys.push(key);
  }
  return filteredKeys;
};

// these should not be included in the active column dropdown / selector
const EXCLUDED_COLUMN_KEYS = ['Name', 'Status', 'Actions', 'CustomLeftColumn'];

export const extractVisibleColumns = (
  columns: IListViewTableColumns,
  activeColumnKeys: string[],
  isSuperUser: boolean = false,
) => {
  const filteredActiveColumnKeys = filterActiveColumnKeys(activeColumnKeys, columns, isSuperUser);
  const visibleColumns: IMenuList = {};
  Object.values(columns).forEach((column) => {
    if (
      !column.isHidden &&
      !EXCLUDED_COLUMN_KEYS.includes(column.key) &&
      (!column.isSuperUserOnly || isSuperUser)
    ) {
      const { key, label } = column;
      visibleColumns[key] = {
        ID: key,
        title: label,
        selected: filteredActiveColumnKeys.includes(key),
      };
    }
  });
  return visibleColumns;
};

export interface IRowsPerPageOption {
  value: RowsPerPage;
  label: string;
}

export const defaultRowsPerPageOptions: IRowsPerPageOption[] = [
  { value: 10, label: '10 Rows' },
  { value: 25, label: '25 Rows' },
  { value: 50, label: '50 Rows' },
];

export const smallTableRowsPerPageOptions: IRowsPerPageOption[] = [
  { value: 5, label: '5 Rows' },
  { value: 10, label: '10 Rows' },
  { value: 25, label: '25 Rows' },
];
